import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Searchbar,
} from '@predoc/cow';

export type AutoCompleteOption<T = unknown> = {
  value: string;
  label: string;
  disabled?: boolean;
  data?: T; // Additional data for custom rendering
};

export type AutoCompleteProps<T = unknown> = {
  label?: string;
  options: AutoCompleteOption<T>[];
  value: AutoCompleteOption<T> | null;
  onValueChange: (value: AutoCompleteOption<T> | null) => void;
  onSearchChange: (searchQuery: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  description?: string;
  loading?: boolean;
  emptyMessage?: string;
  renderOption?: (option: AutoCompleteOption<T>) => React.ReactNode;
  getOptionLabel?: (option: AutoCompleteOption<T>) => string;
  error?: boolean;
  errorMessage?: string;
};

// TODO: This will be move to @predoc/cow
export const AutoComplete = <T = unknown,>({
  label,
  options,
  value,
  onValueChange,
  onSearchChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className = '',
  description,
  loading = false,
  emptyMessage = 'No results found',
  renderOption,
  getOptionLabel,
  error = false,
  errorMessage,
}: AutoCompleteProps<T>) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      onSearchChange(query);

      // Only open dropdown when user starts typing
      if (query.trim() !== '') {
        setOpen(true);
      }
    },
    [onSearchChange],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') e.stopPropagation();
    },
    [],
  );

  const handleValueChange = React.useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find(
        (option) => option.value === selectedValue,
      );
      onValueChange(selectedOption || null);
      setOpen(false);
    },
    [onValueChange, options],
  );

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Don't open dropdown if there's a default value and user hasn't typed anything
      if (newOpen && value && searchQuery.trim() === '') {
        return;
      }
      setOpen(newOpen);
    },
    [value, searchQuery],
  );

  // Reset search query when dropdown closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  return (
    <div className="flex flex-col space-y-1.5">
      {label && (
        <label
          className={cn('text-sm font-medium', error && 'text-destructive')}
        >
          {label}
        </label>
      )}

      <Select
        value={value?.value || ''}
        onValueChange={handleValueChange}
        disabled={disabled}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <SelectTrigger
          className={cn(
            'w-full',
            error && 'border-destructive focus:border-destructive',
            className,
          )}
        >
          <SelectValue placeholder={placeholder}>
            {value && (getOptionLabel ? getOptionLabel(value) : value.label)}
          </SelectValue>
        </SelectTrigger>

        <SelectContent
          className={cn(
            'p-0',
            renderOption
              ? 'w-[400px]'
              : 'w-[var(--radix-select-trigger-width)]',
          )}
          position="popper"
          align="center"
          sideOffset={5}
        >
          <Searchbar
            searchQuery={searchQuery}
            handleInputChange={handleInputChange}
            placeholder={searchPlaceholder}
            onKeyDown={handleKeyDown}
          />

          <SelectGroup className="px-1 py-1">
            {loading ? (
              <div className="text-popover-foreground py-2 text-center text-sm">
                Loading...
              </div>
            ) : options.length > 0 ? (
              options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    'data-[state=checked]:pl-6', // Add space for checkmark
                  )}
                >
                  {renderOption ? renderOption(option) : option.label}
                </SelectItem>
              ))
            ) : (
              <div className="text-popover-foreground py-2 text-center text-sm">
                {emptyMessage}
              </div>
            )}
          </SelectGroup>
        </SelectContent>
      </Select>

      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
      {error && errorMessage && (
        <p className="text-destructive text-sm">{errorMessage}</p>
      )}
    </div>
  );
};
